import {X} from 'lucide-react'
import React, {useState} from 'react'
import {useGlobalSnackbar} from '~/src/context/GlobalSnackbarContext'
import PasswordResetForm from '../../../components/auth/PasswordResetForm'
import {userApi} from '../../../services/userApi'
import VerifyPasswordBody from './VerifyPasswordBody'
import {
  Body,
  ButtonBox,
  ChangePasswordDialogStyles,
  EnterCurrentPasswordText,
  HeaderContainer,
  HeaderText,
  StyledButton,
} from './styles/ChangePasswordDialogStyles'

interface IProps {
  handleCancel(): void
  isOpen: boolean
}

const errorText = {
  sameAsLast3Passwords: 'New password cannot match previous 3 passwords',
}

export default function ChangePasswordDialog({isOpen, handleCancel}: IProps): React.ReactElement {
  const [verifyPasswordStep, setVerifyPasswordStep] = useState(1)
  const [oldPassword, setOldPassword] = useState('')
  const [customMessage, setCustomMessage] = useState('')

  const {showSuccess, showError} = useGlobalSnackbar()

  const handleCancelAndState = () => {
    setVerifyPasswordStep(1)
    setOldPassword('')
    setCustomMessage('')
    handleCancel()
  }

  const handlePasswordChange = async (values: {password: string}) => {
    setCustomMessage('')

    try {
      await userApi.updateUserPassword(values.password)

      showSuccess('Password changed successfully!')
      handleCancelAndState()
    } catch (error: unknown) {
      // Better error handling without unwrap()
      const err = error as {response?: {data?: {message?: string}}}
      const errorMessage = err.response?.data?.message ?? ''

      // Check for specific error about previous passwords
      if (errorMessage.includes('New password cannot match previous 3 passwords')) {
        setCustomMessage(errorText.sameAsLast3Passwords)
        // Remove error after 5 seconds
        setTimeout(() => setCustomMessage(''), 5000)
      } else {
        // Show the actual error message
        showError(new Error(errorMessage || 'Failed to update password'))
      }
    }
  }

  const displayVerifySwitch = () => {
    switch (verifyPasswordStep) {
      case 2:
        return (
          <>
            <EnterCurrentPasswordText style={{marginBottom: '16px'}}>
              New Password
            </EnterCurrentPasswordText>

            <PasswordResetForm
              customMessage={customMessage}
              oldPassword={oldPassword}
              onSubmit={handlePasswordChange}
              isError={false}
              newButtons={
                <ButtonBox style={{marginTop: '20px'}}>
                  <StyledButton
                    data-testid="changePassword-cancel-button"
                    aria-label="changePassword-cancel-button"
                    style={{marginRight: '25px', width: '120px', fontWeight: 'normal'}}
                    onClick={() => {
                      handleCancelAndState()
                    }}
                    variant="outlined"
                    color="secondary"
                    type="button">
                    Cancel
                  </StyledButton>
                  <StyledButton
                    data-testid="changePassword-save-button"
                    aria-label="changePassword-save-button"
                    sx={{color: 'white', textWrap: 'nowrap'}}
                    variant="contained"
                    color="primary"
                    type="submit">
                    Set Password
                  </StyledButton>
                </ButtonBox>
              }
            />
          </>
        )
      default:
        return (
          <VerifyPasswordBody
            handleCancel={handleCancelAndState}
            setCustomMessage={setCustomMessage}
            setOldPassword={setOldPassword}
            setVerifyPasswordStep={setVerifyPasswordStep}
          />
        )
    }
  }

  return (
    <ChangePasswordDialogStyles verifyPasswordStep={verifyPasswordStep} open={isOpen}>
      <HeaderContainer style={{minHeight: '72px', maxHeight: '72px'}}>
        <HeaderText style={{fontWeight: 600, fontSize: '16px'}}>Change Password</HeaderText>
        <X
          aria-label="changePassword-closeDialog-x-button"
          data-testid="changePassword-closeDialog-x-button"
          onClick={() => {
            handleCancelAndState()
          }}
          style={{cursor: 'pointer'}}
          size={20}
        />
      </HeaderContainer>
      <Body style={{paddingTop: verifyPasswordStep === 2 ? '20px' : ''}}>
        {displayVerifySwitch()}
      </Body>
    </ChangePasswordDialogStyles>
  )
}
